import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';

// Register GSAP plugins
gsap.registerPlugin(ScrollTrigger);

// GSAP configuration
export const gsapConfig = {
  // Default animation settings
  defaults: {
    duration: 0.8,
    ease: "power2.out",
  },
  
  // ScrollTrigger defaults
  scrollTrigger: {
    scroller: "body",
    start: "top 80%",
    end: "bottom 20%",
    toggleActions: "play none none reverse",
  },
};

// Utility functions for common animations
export const animations = {
  // Fade in animation
  fadeIn: (element: gsap.DOMTarget, options = {}) => {
    return gsap.fromTo(element, 
      { opacity: 0 },
      { 
        opacity: 1, 
        duration: 0.8,
        ease: "power2.out",
        ...options 
      }
    );
  },

  // Slide up animation
  slideUp: (element: gsap.DOMTarget, options = {}) => {
    return gsap.fromTo(element,
      { y: 50, opacity: 0 },
      {
        y: 0,
        opacity: 1,
        duration: 0.8,
        ease: "power2.out",
        ...options
      }
    );
  },

  // Scale in animation
  scaleIn: (element: gsap.DOMTarget, options = {}) => {
    return gsap.fromTo(element,
      { scale: 0.8, opacity: 0 },
      {
        scale: 1,
        opacity: 1,
        duration: 0.6,
        ease: "back.out(1.7)",
        ...options
      }
    );
  },

  // Parallax effect
  parallax: (element: gsap.DOMTarget, speed = 0.5, options = {}) => {
    return gsap.to(element, {
      yPercent: -50 * speed,
      ease: "none",
      scrollTrigger: {
        trigger: element,
        start: "top bottom",
        end: "bottom top",
        scrub: true,
        ...options
      }
    });
  },

  // Stagger animation for multiple elements
  stagger: (elements: gsap.DOMTarget[], animation: any, stagger = 0.1) => {
    return gsap.fromTo(elements, animation.from, {
      ...animation.to,
      stagger: stagger
    });
  },

  // Magnetic hover effect
  magnetic: (element: HTMLElement) => {
    const handleMouseMove = (e: MouseEvent) => {
      const rect = element.getBoundingClientRect();
      const x = e.clientX - rect.left - rect.width / 2;
      const y = e.clientY - rect.top - rect.height / 2;
      
      gsap.to(element, {
        x: x * 0.1,
        y: y * 0.1,
        duration: 0.3,
        ease: "power2.out"
      });
    };

    const handleMouseLeave = () => {
      gsap.to(element, {
        x: 0,
        y: 0,
        duration: 0.5,
        ease: "power2.out"
      });
    };

    element.addEventListener('mousemove', handleMouseMove);
    element.addEventListener('mouseleave', handleMouseLeave);

    return () => {
      element.removeEventListener('mousemove', handleMouseMove);
      element.removeEventListener('mouseleave', handleMouseLeave);
    };
  }
};

// Initialize scroll-based animations
export const initScrollAnimations = () => {
  // Hero section parallax
  gsap.utils.toArray('.parallax-bg').forEach((element) => {
    animations.parallax(element as HTMLElement, 0.3);
  });

  // Section entrance animations
  gsap.utils.toArray('.section-card').forEach((element, index) => {
    ScrollTrigger.create({
      trigger: element as HTMLElement,
      start: "top 80%",
      onEnter: () => {
        animations.scaleIn(element as HTMLElement, { delay: index * 0.1 });
      }
    });
  });

  // Text reveal animations
  gsap.utils.toArray('.text-reveal').forEach((element) => {
    ScrollTrigger.create({
      trigger: element as HTMLElement,
      start: "top 85%",
      onEnter: () => {
        animations.slideUp(element as HTMLElement);
      }
    });
  });
};

// Cleanup function
export const cleanupGSAP = () => {
  ScrollTrigger.getAll().forEach(trigger => trigger.kill());
  gsap.killTweensOf("*");
};

export default gsap;
